import { useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { Logo } from "@/components/Logo";
import { LogoutButton } from "@/components/LogoutButton";
import { StateMessage } from "@/components/StateMessage";
import { Icon } from "@/components/Icon";
import { PunchLocationLine } from "@/components/PunchLocationLine";
import { PendingPunchStatus, formatPunchTime } from "@/components/PendingPunchStatus";
import { AllocationPanel } from "@/components/AllocationPanel";
import { MyShifts } from "@/components/MyShifts";
import { TimeOffRequests } from "@/components/TimeOffRequests";
import { useMyTime } from "@/hooks/useMyTime";
import { usePunchOutbox } from "@/hooks/usePunchOutbox";
import { ApiError, demoApi, problemMessage } from "@/api/client";
import { punchOutbox, type AllocationRefusal, type PunchRefusal } from "@/offline/punchOutbox";
import type {
  AllocationItem,
  JobCode,
  PayPeriod,
  PunchRequest,
  SignTimesheetRequest,
  TimeEntry,
  TimeEntryStatus,
} from "@/api/types";
import { formatDateShort, formatOfflineDelay } from "@/lib/format";
import "./TimeClock.css";

/**
 * The employee's own time screen — `/time`.
 *
 * WHAT IS NOT ON THIS SCREEN IS THE DESIGN.
 *
 * No wage, no rate, no net pay, no colleague, no farm total, no nav to anything that
 * carries them. This is the first surface in the product a WORKER signs into, and
 * `db/migrations/0008_time_tracking.sql` opens by saying that an employee who can read
 * the farm's books is a worse outcome than having no time tracking at all. The server
 * enforces that (every `/api/demo/*` route is owner-only unless it opts in by name), but
 * a screen that rendered a nav bar full of doors returning 403 would still teach a worker
 * exactly what exists behind them. So this renders OUTSIDE the app shell, like
 * `/entry` — no sidebar, no bottom nav, nowhere to wander.
 *
 * PHONE FIRST, and not merely narrow: this is used one-handed in a yard, possibly in
 * gloves, possibly at 5am. The one thing a person came here to do is a single tap on the
 * largest control on the page.
 *
 * See TimeClock.css for the layout rules and `docs`-worthy notes on tap sizing.
 */

/** Longest we will make somebody wait for a GPS fix before punching them in anyway. */
const LOCATION_TIMEOUT_MS = 6000;

type PunchKind = "in" | "out";

export function TimeClock({ onLogout }: { onLogout: () => Promise<void> }) {
  const { data, status, error, reload } = useMyTime();

  /** ef-017 — this device's queued, not-yet-confirmed punches, oldest first. */
  const punchQueue = usePunchOutbox();
  /** A refused REPLAY (a permanent 4xx, not a network problem) — see punchOutbox.ts's
   *  own remarks on why that is surfaced as an event rather than read off the queue,
   *  which has already dropped the item by the time this fires. */
  const [refusal, setRefusal] = useState<PunchRefusal | null>(null);
  /** ef-024 — a refused ALLOCATION for a punch-out that had already landed by the time
   *  the paired PUT ran (`punchOutbox.ts`'s own remarks on why this is a different shape
   *  from `PunchRefusal`). */
  const [allocationRefusal, setAllocationRefusal] = useState<AllocationRefusal | null>(null);

  /**
   * ef-024 — the farm's own active job codes, for the allocation panel's quick-pick
   * buttons and split selects. Loaded ONCE, not re-fetched or filtered per entry: the
   * same list has to answer for every panel this page opens
   * (`worker-allocation.spec.ts`'s own canary, C2, picks a THIRD fixture on a SECOND
   * entry and requires it enabled).
   */
  const [jobCodes, setJobCodes] = useState<JobCode[]>([]);

  /**
   * ef-024 — the entry currently offering "What was this for?", or null when nothing is
   * asking. `hours` is that entry's own total (typed on manual, derived by the server on
   * an online punch-out, or this device's own estimate for one still queued); `save` is
   * what a choice resolves into — a real `PUT /allocations` for an already-submitted row,
   * or a local attach onto the outbox for one that has not landed yet. Cleared on success
   * by whichever caller below opened it — `AllocationPanel` itself never reaches in.
   */
  const [activeAllocation, setActiveAllocation] = useState<{
    hours: number;
    save: (allocations: AllocationItem[]) => Promise<void>;
  } | null>(null);

  const [busy, setBusy] = useState<PunchKind | "manual" | "sign" | null>(null);

  /**
   * What the worker is typing about the shift they are about to close.
   *
   * Lives on the clock section rather than in a dialog. A modal between a thumb and "Clock
   * out" is a modal a tired person dismisses, and then the note is the thing that did not
   * get written — the box has to be visible while the shift is running, so writing in it is
   * a choice rather than an interruption.
   */
  const [punchNote, setPunchNote] = useState("");

  /**
   * Minutes the worker says they were not working, typed at punch-out.
   *
   * A string, not a number, because "" is a real state — it means they have not said, which
   * is different from zero and is what gets sent as nothing at all. Parsing early would
   * collapse the two.
   */
  const [punchBreak, setPunchBreak] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const openShift = data?.openShift ?? null;
  const offersPunch = data?.mode === "punch" || data?.mode === "both";
  const offersManual = data?.mode === "manual" || data?.mode === "both";

  /**
   * The clock's LOCAL truth: server truth (`openShift`), overridden by whatever this
   * device most recently queued but has not yet had confirmed. Both directions matter —
   * a queued clock-IN with no server shift yet must still read as running (AC1's "offers
   * Clock out NEXT, even though the server has not confirmed the shift"), and a queued
   * clock-OUT against a real running shift must immediately offer Clock in again, the
   * same "opposite action next" rule applied the other way.
   *
   * The LAST queued item wins, not "any queued item" — the in-then-out sequence AC3
   * drives with a fake clock leaves both in the queue at once, and the one that decides
   * what this screen offers next is whichever the worker tapped most recently.
   */
  const lastQueuedPunch = punchQueue.length > 0 ? punchQueue[punchQueue.length - 1] : null;
  const effectiveOpen = lastQueuedPunch ? lastQueuedPunch.kind === "in" : openShift !== null;
  /** What `<Elapsed>` counts from — the server's own `startedAt` once it exists, and
   *  until then the phone's own tapped time for a queued clock-in that has not been
   *  confirmed yet. Never fabricated when nothing is actually running. */
  const elapsedSince = effectiveOpen ? (openShift?.startedAt ?? lastQueuedPunch?.recordedAt ?? null) : null;

  useEffect(() => {
    return punchOutbox.subscribeRefusals((r) => {
      setActionError(null);
      setConfirmation(null);
      setRefusal(r);
      // The refusal means the server's own idea of the world moved (someone else
      // closed the shift, etc.) — re-read rather than trust whatever this screen was
      // showing before the reply came back, the same reasoning `punch()`'s own 409
      // branch below already follows for an ONLINE conflict.
      void reload();
    });
  }, [reload]);

  /** ef-024 — a punch-out that already landed, whose PAIRED allocation was then
   *  refused (`punchOutbox.ts`'s own remarks on why this is never read as the punch-out
   *  itself failing). */
  useEffect(() => {
    return punchOutbox.subscribeAllocationRefusals((r) => {
      setAllocationRefusal(r);
    });
  }, []);

  /**
   * ef-024 — this farm's active job codes, fetched once WHILE ONLINE so an offline
   * punch-out's own panel (`queueLocally` above) still has something to offer — there is
   * nowhere to fetch them FROM once the device has actually gone offline, which is
   * exactly the moment the panel most needs them.
   *
   * NEVER ATTEMPTED KNOWN-OFFLINE — mirrors `punch()`'s own `!navigator.onLine` check,
   * for the same reason: a request that cannot succeed has nothing to prove and only
   * adds a browser-logged network error to a screen that has to stay clean while offline
   * (`offline-punch.spec.ts`'s own AC9 gate — a reload while offline, immediately before
   * this effect would otherwise have fired, is exactly the case that caught this). Retried
   * on the same `'online'` signal `drainTriggers.ts` already uses elsewhere. A worker who
   * still cannot reach it is not blocked from clocking in or out either way — Skip stays
   * legal with an empty list (contract Assumption A8).
   * @ui-check: this must stay reachable with no live API — the offline-reload path
   * (AC8/AC9) depends on it never attempting a request while `navigator.onLine` is false.
   */
  useEffect(() => {
    let cancelled = false;
    function load(): void {
      if (!navigator.onLine) return;
      demoApi
        .getWorkerJobCodes()
        .then((codes) => {
          if (!cancelled) setJobCodes(codes);
        })
        .catch(() => {
          /* Degrades to "nothing to pick from yet" — see the effect's own remark above. */
        });
    }
    load();
    window.addEventListener("online", load);
    return () => {
      cancelled = true;
      window.removeEventListener("online", load);
    };
  }, []);

  /**
   * `reload()` fires the moment the queue goes from something to nothing — never on a
   * partial drain, which would re-read while the REMAINING item is still what decides
   * `effectiveOpen` above. Without this, the instant the last queued punch drains in the
   * background, this screen would keep rendering off its now-stale `data.openShift`
   * (`effectiveOpen` falls back to it exactly when the queue is empty) until something
   * else happened to trigger a re-read. Mirrors `punch()`'s own post-action `reload()` —
   * "after a drain, reload() as today."
   */
  const previousQueueLength = useRef(0);
  useEffect(() => {
    if (previousQueueLength.current > 0 && punchQueue.length === 0) {
      void reload();
    }
    previousQueueLength.current = punchQueue.length;
  }, [punchQueue.length, reload]);

  async function punch(kind: PunchKind, workerNote = "", breakMinutes = ""): Promise<void> {
    // THE LOCAL ONE-OPEN-SHIFT RULE (offline-punch.spec.ts's own canary). The button
    // that would let a worker tap the same kind twice is never RENDERED while
    // `effectiveOpen` already says so (below) — this is the second line of defence for
    // a race that gets past that render (e.g. a fast double-tap before React commits
    // the new state), never the only one.
    if (kind === "in" && effectiveOpen) return;
    if (kind === "out" && !effectiveOpen) return;

    setBusy(kind);
    setActionError(null);
    setConfirmation(null);
    // Captured BEFORE anything async, including the geolocation lookup just below (which
    // can take up to LOCATION_TIMEOUT_MS). offline-punch.spec.ts's own AC1 checks the
    // server's startedAt against the moment of the TAP, to the minute — deriving this
    // any later would let a slow GPS fix quietly shift the stamp a worker is trusting to
    // be honest about when their day started.
    const tappedAt = new Date();
    const note = kind === "out" ? workerNote.trim() : "";
    // Blank stays UNSENT rather than becoming 0. Zero is a claim the worker took no
    // break; saying nothing is a different fact, and the column keeps them apart.
    const mins = Number.parseFloat(breakMinutes);
    const outFields =
      kind === "out"
        ? { workerNote: note === "" ? undefined : note, breakMinutes: Number.isFinite(mins) ? mins : undefined }
        : {};
    const where = await currentLocation();

    /**
     * Queues this tap without ever attempting to send it — used both when the device
     * already KNOWS it is offline (below) and when a live attempt just proved it
     * (the catch branch further down).
     */
    const queueLocally = async (): Promise<void> => {
      const clientPunchId = await punchOutbox.enqueue({
        kind,
        recordedAt: tappedAt.toISOString(),
        latitude: where?.latitude,
        longitude: where?.longitude,
        ...outFields,
      });
      if (kind === "out") {
        setPunchNote("");
        setPunchBreak("");
        // ef-024 — the SAME "what was this for?" offer a live punch-out gets, from a
        // LOCAL estimate: the server has not seen this shift close yet, so there is no
        // real hours figure to ask against. The reference point is the shift's own
        // CONFIRMED start (exactly what `elapsedSince` above already trusts) — never a
        // queued, not-yet-confirmed clock-in, which could still be corrected or refused
        // before this punch-out ever replays. No confirmed start means no honest
        // estimate, so the panel is simply not offered rather than guessed at.
        const startedAt = openShift?.startedAt ?? null;
        if (startedAt !== null) {
          const estimatedHours =
            Math.round(((tappedAt.getTime() - new Date(startedAt).getTime()) / 3_600_000) * 100) /
            100;
          if (estimatedHours > 0) {
            setActiveAllocation({
              hours: estimatedHours,
              save: (allocations) => punchOutbox.attachAllocations(clientPunchId, allocations),
            });
          }
        }
      }
    };

    try {
      // KNOWN OFFLINE — never even attempt the request. Mirrors drainTriggers.ts's own
      // "genuinely offline: stop burning retries" check on the REPLAY side, applied here
      // to the FIRST attempt: a fetch made while `navigator.onLine` is already false has
      // nothing to prove and nowhere to go, so there is no reason to let it sit in
      // flight — and every genuinely-offline AC in offline-punch.spec.ts (AC1, AC3, AC7,
      // the canary) drives this exact signal via `context.setOffline(true)`. AC4's proxy
      // outage and AC5's hang are BOTH cases where the phone's own network is fine
      // (`navigator.onLine` stays true), so neither takes this path — they still make
      // the live attempt and are caught below exactly as before.
      if (!navigator.onLine) {
        await queueLocally();
        return;
      }

      if (kind === "in") {
        // No note on the way IN. There is nothing to explain at the start of a shift, and
        // a box between a worker and clocking in is one more thing between them and being
        // paid. The server ignores one if it arrives; this simply never sends one.
        //
        // NEVER `clientPunchId`/`recordedAt` here — this is the FIRST, LIVE attempt. Those
        // two travel only on a REPLAY from `punchOutbox` (see the catch branch below), so
        // an ordinary online punch is byte-for-byte the request it has always been.
        await demoApi.punchIn(where);
        setConfirmation("Clocked in. Have a good one.");
      } else {
        const result = await demoApi.punchOut({ ...where, ...outFields });
        setConfirmation(
          result.recorded
            ? `Clocked out — ${formatHours(result.hours)} sent to your employer to approve.`
            : // Closed, but nothing filed. Said out loud rather than swallowed: the tap DID
              // something (the shift is over), and a worker who taps twice by accident should
              // not be left wondering whether a claim went in. It also replaces the old
              // message's small lie — "0.00 hrs sent to your employer to approve" — about a
              // claim that had nothing in it.
              "Clocked out. That shift was too short to record, so nothing was sent for approval.",
        );
        // ef-024 — offered only for a shift that actually filed something; a discarded
        // 0.00h shift has no row to allocate, and `result.id` agrees (absent on that
        // path — see `PunchOutResult`'s own remark).
        if (result.recorded && result.id) {
          const entryId = result.id;
          setActiveAllocation({
            hours: result.hours,
            save: async (allocations) => {
              await demoApi.putTimeAllocations(entryId, { allocations });
              await reload();
            },
          });
        }
      }
      if (kind === "out") {
        setPunchNote("");
        setPunchBreak("");
      }
      await reload();
    } catch (err) {
      if (err instanceof ApiError && err.neverReachedTheServer) {
        // OFFLINE PUNCH OUTBOX (ef-017). Mirrors of-002's own rule for the entry
        // outbox: a tap the outbox accepted must never read as an error. NEVER a
        // `reload()` here — nothing on the server changed, and `PendingPunchStatus`
        // below (fed by `punchQueue`, which just grew) is the on-device signal that the
        // tap counted, exactly the way `pendingPunch` clearing is the signal a drain
        // succeeded.
        await queueLocally();
        return;
      }
      setActionError(
        problemMessage(err, kind === "in" ? "Could not clock in." : "Could not clock out."),
      );
      // A 409 means this screen's idea of the world is stale (already clocked in, or the
      // shift was closed elsewhere). Re-reading is the only honest response — the error
      // above says what happened, and the controls below then match reality.
      await reload();
    } finally {
      setBusy(null);
    }
  }

  async function submitManual(
    workDate: string,
    hours: number,
    workerNote: string,
  ): Promise<void> {
    setBusy("manual");
    setActionError(null);
    setConfirmation(null);
    try {
      // Trimmed here AND on the server. The server's is the one that counts — this one
      // exists so a worker who typed only spaces does not watch a note vanish silently
      // between their screen and the farmer's queue.
      const note = workerNote.trim();
      const { id } = await demoApi.recordManualTime({
        workDate,
        hours,
        workerNote: note === "" ? undefined : note,
      });
      setConfirmation(`${formatHours(hours)} sent to your employer to approve.`);
      await reload();
      // ef-024 — offer "what was this for?" the moment the row exists. A manual entry
      // always has a real id and a real, already-typed total the instant this resolves,
      // unlike a queued punch-out — so this is always the "server" shape, never a local
      // attach.
      setActiveAllocation({
        hours,
        save: async (allocations) => {
          await demoApi.putTimeAllocations(id, { allocations });
          await reload();
        },
      });
    } catch (err) {
      setActionError(problemMessage(err, "Could not save those hours."));
    } finally {
      setBusy(null);
    }
  }

  async function sign(body: SignTimesheetRequest): Promise<void> {
    setBusy("sign");
    setActionError(null);
    setConfirmation(null);
    try {
      const period = await demoApi.signTimesheet(body);
      setConfirmation(
        `Signed — ${formatHours(period.signature!.signedHours)} for ${formatPeriod(period)}.`,
      );
      // Re-read rather than patching state from the response. The card must render what
      // the SERVER holds, including a staleness verdict that only it can compute.
      await reload();
    } catch (err) {
      setActionError(problemMessage(err, "Could not save your signature."));
    } finally {
      setBusy(null);
    }
  }

  const header = (
    <header className="time__header">
      <Logo size={32} onDark />
      <LogoutButton onLogout={onLogout} className="time__logout" />
    </header>
  );

  if (status === "loading") {
    return (
      <div className="time time--centered" data-state="loading">
        {header}
        <StateMessage tone="loading" title="Loading your time…" />
      </div>
    );
  }

  if (status === "error" || !data) {
    return (
      <div className="time time--centered" data-state="error">
        {header}
        <StateMessage
          tone="error"
          title="Can't load your time right now."
          detail={error ?? "Check your connection and try again."}
          action={
            <button className="btn btn--primary" onClick={() => void reload()}>
              Try again
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="time" data-state="ready">
      {header}

      <main className="time__main">
        {/* The clock. data-state is the tester's ground truth for whether a shift is
            running (rules/ui-breadcrumbs §1) — read off the DOM, never inferred from
            which words happen to be on the button. */}
        {offersPunch && (
          <section
            className="time__clock"
            aria-labelledby="clock-heading"
            data-state={effectiveOpen ? "running" : "idle"}
          >
            <h1 id="clock-heading" className="time__heading">
              {effectiveOpen ? "You're clocked in" : "Ready to start"}
            </h1>

            {/* ef-017 — this device's own queued taps, shown as local truth rather than
                as an error (ui-breadcrumbs #1: role="status" + data-state="pending",
                never role="alert" — nothing failed). */}
            <PendingPunchStatus items={punchQueue} />

            {elapsedSince && <Elapsed since={elapsedSince} />}

            {/*
              ONLY WHILE A SHIFT IS RUNNING (server-confirmed OR a locally queued clock-in
              not yet confirmed — see `effectiveOpen`). There is nothing to explain before
              one starts, and a note box on the "Clock in" screen is one more thing between
              a worker and getting paid. It appears when the thing worth explaining has
              happened.
            */}
            {effectiveOpen && (
              <>
                <label className="time__field time__field--break">
                  <span className="time__field-label">
                    Break taken, in minutes (optional)
                  </span>
                  <input
                    className="time__input time__input--hours"
                    type="number"
                    inputMode="numeric"
                    step="5"
                    min="0"
                    placeholder="30"
                    value={punchBreak}
                    disabled={busy !== null}
                    onChange={(e) => setPunchBreak(e.target.value)}
                  />
                  {/*
                    SAID PLAINLY, NEXT TO THE FIELD. A worker typing 30 here reasonably
                    expects to be paid half an hour less — and they are not. EasyFarm records
                    the break and does not touch the hours (0020), so the screen has to say
                    so rather than leave them to find out on a payslip.

                    ⚠️ AND IT CLAIMS NOTHING ABOUT BREAK LAW. Not "required", not "entitled",
                    not "compliant" — this product has made no such determination, and 0020
                    says why it must not be made here.
                  */}
                  <span className="time__field-hint">
                    Recorded for your employer. Your hours are not reduced by it.
                  </span>
                </label>

                <NoteField
                  value={punchNote}
                  onChange={setPunchNote}
                  disabled={busy !== null}
                  label="Anything your employer should know? (optional)"
                />
              </>
            )}

            <button
              type="button"
              className={`btn time__punch ${effectiveOpen ? "time__punch--out" : "time__punch--in"}`}
              disabled={busy !== null || punchNote.trim().length > MAX_NOTE}
              onClick={() => void punch(effectiveOpen ? "out" : "in", punchNote, punchBreak)}
            >
              {busy === "in" || busy === "out"
                ? "One moment…"
                : effectiveOpen
                  ? "Clock out"
                  : "Clock in"}
            </button>

            {/*
              Said plainly, next to the button that does it, rather than buried in a
              policy nobody reads. The migration's note on location is the source: a
              browser CANNOT track in the background — navigator.geolocation runs only
              while this page is open — so "one stamp, not a trail" is a statement about
              what is buildable here, not a promise we are asking to be trusted on.
            */}
            <p className="time__location-note">
              Clocking {effectiveOpen ? "out" : "in"} records where you are at that moment, if
              your phone offers it. It&apos;s a single stamp — you are not tracked while
              you work, and you can say no and still clock {effectiveOpen ? "out" : "in"}.
            </p>
          </section>
        )}

        {offersManual && (
          <ManualHours
            // On a MANUAL-ONLY farm — the schema's default, so the common case — the
            // punch card above does not render and this section carries the page's only
            // heading. It has to be the h1 then, or the screen starts at h2 and a screen
            // reader's document outline has no top. Not a hypothetical: `manual` is what
            // every entity gets until somebody changes it.
            primary={!offersPunch}
            busy={busy === "manual"}
            disabled={busy !== null}
            onSubmit={submitManual}
          />
        )}

        {/*
          ef-024 — "What was this for?" Offered the moment a shift closes (a real one,
          online or still queued) or an hours entry is filed — never found later by going
          looking for it. `activeAllocation` is cleared here on SUCCESS only; a failed
          save leaves the panel open with the server's own words (`AllocationPanel`'s own
          contract).
        */}
        {activeAllocation && (
          <AllocationPanel
            hours={activeAllocation.hours}
            jobCodes={jobCodes}
            onSave={async (allocations) => {
              await activeAllocation.save(allocations);
              setActiveAllocation(null);
            }}
            onSkip={() => setActiveAllocation(null)}
          />
        )}

        {/*
          ef-024 — a punch-out that already landed, whose paired allocation was then
          refused. Same persistent/dismissable treatment as the punch refusal below, and
          for the identical reason: this can land after the panel that caused it has long
          since closed.
        */}
        {allocationRefusal && (
          <div className="time__refusal" role="alert">
            <p className="time__refusal-text">
              Your job code split for that day was refused: {allocationRefusal.reason}
            </p>
            <button
              type="button"
              className="btn btn--ghost time__refusal-dismiss"
              onClick={() => setAllocationRefusal(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        {/*
          A refused REPLAY (ef-017) — a permanent 4xx the outbox drops, never one this
          screen can retry by itself. PERSISTENT AND DISMISSABLE, unlike `actionError`
          below: that message describes THIS render's failed attempt and is naturally
          replaced by the next one, but a refusal can land minutes after the tap that
          caused it, with the worker looking at something else entirely — it must not
          vanish on its own before they have even seen it.

          @ui-check: the sentence below must contain the literal word "refused" —
          offline-punch.spec.ts's `refusalAlert` locator filters on it to tell this
          apart from an ordinary online failure (`actionError`, also `role="alert"`).
        */}
        {refusal && (
          <div className="time__refusal" role="alert">
            <p className="time__refusal-text">
              Your clock-{refusal.kind} at {formatPunchTime(refusal.recordedAt)} was refused:{" "}
              {refusal.reason}
            </p>
            <button
              type="button"
              className="btn btn--ghost time__refusal-dismiss"
              onClick={() => setRefusal(null)}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Both messages live here, between the controls and the history, so a person who
            just tapped sees the outcome without scrolling in either direction. */}
        {actionError && (
          <p className="time__error" role="alert">
            {actionError}
          </p>
        )}
        {confirmation && !actionError && (
          <p className="time__confirmation" role="status">
            <Icon name="check" size={16} strokeWidth={2.6} />
            {confirmation}
          </p>
        )}

        <SignCard
          period={data.period}
          busy={busy === "sign"}
          disabled={busy !== null}
          onSign={sign}
        />

        <RecentDays entries={data.recent} />

        {/* ef-040-4 — the worker's own upcoming shifts (read-only) and their own time-off
            requests (submit + history), each a self-contained region with its own fetch,
            its own offline guard, and its own error/empty state — a failure in one never
            takes the clock, or the other, down with it. */}
        <MyShifts />
        <TimeOffRequests />
      </main>
    </div>
  );
}

/**
 * The running shift's elapsed time, ticking once a second.
 *
 * A COURTESY, NOT THE FIGURE. It is computed from this phone's clock against the
 * server's start time, so a device with a wrong clock shows a wrong number here — which
 * is exactly why the payable hours are derived server-side from two server timestamps
 * and never sent from the browser. Labelled "so far" rather than presented as a total
 * for the same reason.
 */
function Elapsed({ since }: { since: string }) {
  const startedAt = useMemo(() => new Date(since).getTime(), [since]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Clamped at zero: a phone whose clock is a minute behind the server would otherwise
  // render a negative shift, which reads as a bug rather than as clock skew.
  const seconds = Math.max(0, Math.round((now - startedAt) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return (
    <p className="time__elapsed">
      {/* aria-live off by design: a timer announcing itself every second makes a screen
          reader unusable. The value is decoration around the button, not the news. */}
      <span className="time__elapsed-value">
        {hours}h {String(minutes).padStart(2, "0")}m
      </span>
      <span className="time__elapsed-label">
        so far · started {new Date(since).toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
        })}
      </span>
    </p>
  );
}

/**
 * Typed hours for a day — the mode for farms that want no clock at all.
 *
 * Native `<input type="date">` and `type="number"`, not a bespoke picker: on a phone
 * both open the OS control the person already knows, and the min/max attributes give the
 * browser the same bounds the server enforces. Client bounds are a courtesy that saves a
 * round trip; the server's are the rule, and its message is what gets shown when they
 * disagree.
 */
/**
 * The longest note the server will take. Mirrors `TimeEndpoints.MaxNoteLength` and 0019's
 * CHECK.
 *
 * Duplicated here rather than fetched, and the duplication is the honest kind: this number
 * only decides when to show a counter and grey out a button. The server's copy is the one
 * that refuses, so a drift between them costs a worker a clear message and never a wrong
 * record.
 */
const MAX_NOTE = 500;

/**
 * The note box, shared by the punch clock and the manual form.
 *
 * ONE COMPONENT because there is one rule: optional, capped, and never blocking. Two copies
 * would drift on the part that matters — whether an empty note is allowed — and the answer
 * has to be the same on both surfaces or a worker learns one behaviour and meets the other.
 *
 * The counter appears only as the limit gets close. A character count sitting under an empty
 * box reads as a requirement, on a field whose whole point is that most days need nothing.
 */
function NoteField({
  value,
  onChange,
  disabled,
  label = "Anything about this day? (optional)",
}: {
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
  label?: string;
}) {
  const length = value.trim().length;
  const tooLong = length > MAX_NOTE;
  const nearLimit = length > MAX_NOTE - 100;

  return (
    <label className="time__field time__field--note">
      <span className="time__field-label">{label}</span>
      <textarea
        className="time__note-input"
        value={value}
        rows={2}
        disabled={disabled}
        /* No maxLength. A hard truncate silently eats the end of a sentence somebody is
           still typing; showing the overage and refusing the submit tells them what to do. */
        aria-invalid={tooLong || undefined}
        aria-describedby={nearLimit ? "note-count" : undefined}
        placeholder="Worked through lunch, covered a shift, wrong day…"
        onChange={(e) => onChange(e.target.value)}
      />
      {nearLimit && (
        <span
          id="note-count"
          className={`time__note-count${tooLong ? " time__note-count--over" : ""}`}
          role={tooLong ? "alert" : "status"}
        >
          {tooLong
            ? `${length - MAX_NOTE} characters too long — trim it to send.`
            : `${MAX_NOTE - length} characters left.`}
        </span>
      )}
    </label>
  );
}

function ManualHours({
  primary,
  busy,
  disabled,
  onSubmit,
}: {
  /** True when this is the only recording mode, so this heading is the page's h1. */
  primary: boolean;
  busy: boolean;
  disabled: boolean;
  onSubmit: (workDate: string, hours: number, workerNote: string) => Promise<void>;
}) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const earliest = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().slice(0, 10);
  }, []);

  const [workDate, setWorkDate] = useState(today);
  const [hours, setHours] = useState("");
  const [note, setNote] = useState("");

  const parsed = Number.parseFloat(hours);
  /* The note is NEVER part of canSubmit. It is optional, and a day that does not need
     explaining is the normal day — gating the button on it would make every entry feel like
     it needed a reason. Over-length is refused by the server with the complaint on the
     field; the counter below is what stops anyone reaching that. */
  const canSubmit = Number.isFinite(parsed) && parsed > 0 && parsed <= 16 && workDate !== "";
  const noteTooLong = note.trim().length > MAX_NOTE;

  return (
    <section className="time__manual" aria-labelledby="manual-heading">
      <Heading
        level={primary ? 1 : 2}
        id="manual-heading"
        className={`time__heading${primary ? "" : " time__heading--sub"}`}
      >
        {/* The wording changes with the level, because "instead" only means anything
            next to a clock. On a manual-only farm there is nothing to say "instead" of. */}
        {primary ? "Enter your hours" : "Enter hours instead"}
      </Heading>

      <form
        className="time__manual-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit || disabled || noteTooLong) return;
          void onSubmit(workDate, parsed, note).then(() => {
            setHours("");
            setNote("");
          });
        }}
      >
        <label className="time__field">
          <span className="time__field-label">Day worked</span>
          <input
            className="time__input"
            type="date"
            value={workDate}
            min={earliest}
            max={today}
            onChange={(e) => setWorkDate(e.target.value)}
          />
        </label>

        <label className="time__field">
          <span className="time__field-label">Hours</span>
          <input
            className="time__input time__input--hours"
            type="number"
            inputMode="decimal"
            step="0.25"
            min="0.25"
            max="16"
            placeholder="8"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
          />
        </label>

        <NoteField value={note} onChange={setNote} disabled={disabled} />

        <button
          type="submit"
          className="btn btn--primary time__manual-submit"
          disabled={!canSubmit || disabled || noteTooLong}
        >
          {busy ? "Sending…" : "Send for approval"}
        </button>
      </form>

      <p className="time__hint">
        You can enter the last 14 days. Ask your employer to add anything older.
      </p>
    </section>
  );
}

/**
 * `<h1>` or `<h2>`, chosen by the caller. Written out rather than reached for a library:
 * one screen needs one heading to change level depending on which recording modes the
 * farm offers, and a document that skips from nothing to h2 is a real outline defect
 * rather than a style question.
 */
function Heading({
  level,
  id,
  className,
  children,
}: {
  level: 1 | 2;
  id: string;
  className: string;
  children: ReactNode;
}) {
  const Tag = level === 1 ? "h1" : "h2";
  return (
    <Tag id={id} className={className}>
      {children}
    </Tag>
  );
}

/**
 * "Sign my hours" — the worker confirming this period's hours are right.
 *
 * WHAT THIS IS, IN THE WORDS THE SCREEN IS ALLOWED TO USE. The worker confirmed these
 * hours are correct, on this date. Nothing here says "H-2A", "compliant", or "required":
 * 20 CFR 655.122(j) obliges the EMPLOYER to furnish a written statement of hours and
 * earnings and contains no signing requirement at all. Signed daily records are ordinary
 * farm-labour-contractor practice and the strongest evidence in a wage investigation —
 * which is why this exists — but the screen states what happened, not a compliance claim.
 * Same discipline as "Projected, not filed" on the tax screens.
 *
 * WHY THE FROZEN FIGURE IS RENDERED, NOT TODAY'S. Once signed, the big number is
 * `signedHours` — what the worker actually agreed to. Showing today's total in its place
 * would quietly restate their signature over hours they never saw, which is the exact
 * forgery the frozen snapshot exists to prevent. When the two differ the card says so and
 * shows both.
 *
 * `data-state` is the tester's ground truth (rules/ui-breadcrumbs §1): `empty` (nothing
 * recorded yet), `unsigned`, `signed`, `stale`. Never inferred from which words are on
 * screen.
 */
function SignCard({
  period,
  busy,
  disabled,
  onSign,
}: {
  period: PayPeriod;
  busy: boolean;
  disabled: boolean;
  onSign: (body: SignTimesheetRequest) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);

  const signature = period.signature;
  const state = signature === null
    ? period.dayCount === 0 ? "empty" : "unsigned"
    : signature.isStale ? "stale" : "signed";

  // Nothing to agree to. Offering a button that signs zero hours would let somebody
  // produce a signature that attests to nothing — worse than no button, because it looks
  // like the feature working.
  if (state === "empty") {
    return (
      <section className="time__sign" aria-labelledby="sign-heading" data-state="empty">
        <h2 id="sign-heading" className="time__heading time__heading--sub">
          Sign your hours
        </h2>
        <p className="time__sign-empty">
          Nothing recorded for {formatPeriod(period)} yet. Once you have hours in this
          period you can sign them here.
        </p>
      </section>
    );
  }

  return (
    <section className="time__sign" aria-labelledby="sign-heading" data-state={state}>
      <h2 id="sign-heading" className="time__heading time__heading--sub">
        Sign your hours
      </h2>

      <p className="time__sign-period">{formatPeriod(period)}</p>

      <p className="time__sign-total">
        <span className="time__sign-hours">
          {formatHours(signature === null ? period.hours : signature.signedHours)}
        </span>
        {/*
          DAYS, and the figure behind it now counts distinct DATES.

          It counted rows until 2026-09-10 and rendered "3055.85 hours across 2158 days" for
          a fortnight — nothing in the schema makes one row one day, since the only unique
          index is one OPEN shift per employee, so clocking out for lunch and back in writes
          two rows for one date. The word was corrected first, on 2026-09-09, because the
          figure is persisted and needed its own reviewable change; 0021 is that change, and
          the word goes back to the one 0018 always used.
        */}
        <span className="time__sign-days">
          across{" "}
          {signature === null ? period.dayCount : signature.signedDayCount}{" "}
          {(signature === null ? period.dayCount : signature.signedDayCount) === 1
            ? "day"
            : "days"}
        </span>
      </p>

      {signature !== null && (
        <p className="time__sign-receipt" data-stale={signature.isStale}>
          {!signature.isStale && <Icon name="check" size={16} strokeWidth={2.6} />}
          <span>
            You confirmed these hours were correct on{" "}
            {new Date(signature.signedAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
            {signature.method === "typed" && signature.typedName
              ? `, as ${signature.typedName}`
              : signature.method === "drawn"
                ? ", by signing"
                : ""}
            .
          </span>
        </p>
      )}

      {/*
        STALENESS IS SHOWN, NOT SILENTLY REPAIRED. The days this signature covered have
        changed since it was given. Moving the frozen figure to match would destroy the
        only thing a signature is worth; hiding the difference would be worse. So both
        numbers are on screen and the worker is asked to look and sign again.
      */}
      {signature?.isStale && (
        <p className="time__sign-stale" role="status">
          Your hours have changed since you signed — they are now{" "}
          {formatHours(signature.currentHours)} across {signature.currentDayCount}{" "}
          {/* Distinct dates on both sides. This sentence is what a worker reads when their
              signature has gone stale and they are deciding whether the new figure looks
              right — the frozen day count and this one must be counting the same thing. */}
          {signature.currentDayCount === 1 ? "day" : "days"}. Have a look, then sign
          again if they are right.
        </p>
      )}

      {!open && (
        <button
          type="button"
          className="btn btn--primary time__sign-button"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          {signature === null ? "Sign these hours" : "Sign again"}
        </button>
      )}

      {open && (
        <SignaturePanel
          busy={busy}
          disabled={disabled}
          onCancel={() => setOpen(false)}
          onSign={async (body) => {
            await onSign(body);
            setOpen(false);
          }}
        />
      )}

      {/*
        What signing IS, said next to the control that does it rather than in a policy
        nobody opens — and what it is NOT, because the farmer's approval is a separate act
        that still has to happen.
      */}
      <p className="time__sign-note">
        Signing records that you agree these hours are right. It is not approval — your
        employer still reviews them.
      </p>
    </section>
  );
}

/** The two ways a worker may sign. Both are ordinary e-signature practice. */
type SignMode = "draw" | "type";

/**
 * The signing controls themselves.
 *
 * BOTH ROUTES COUNT, and neither is the "real" one. Drawing with a finger is what people
 * expect on a phone; a typed name plus a deliberate tap is what works with a keyboard, a
 * screen reader, or gloves on a cold morning. Offering only the canvas would make this
 * feature unusable for exactly the people most likely to need it.
 */
function SignaturePanel({
  busy,
  disabled,
  onCancel,
  onSign,
}: {
  busy: boolean;
  disabled: boolean;
  onCancel: () => void;
  onSign: (body: SignTimesheetRequest) => Promise<void>;
}) {
  const [mode, setMode] = useState<SignMode>("draw");
  const [typedName, setTypedName] = useState("");
  const [drawing, setDrawing] = useState<string | null>(null);

  const canSubmit = mode === "type" ? typedName.trim().length > 0 : drawing !== null;

  return (
    <div className="time__sign-panel" data-mode={mode}>
      {/* A radiogroup rather than tabs: these are two values of one choice, and that is
          what a screen reader should hear. */}
      <div className="time__sign-modes" role="radiogroup" aria-label="How to sign">
        <button
          type="button"
          role="radio"
          aria-checked={mode === "draw"}
          className="time__sign-mode"
          onClick={() => setMode("draw")}
        >
          Draw it
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={mode === "type"}
          className="time__sign-mode"
          onClick={() => setMode("type")}
        >
          Type my name
        </button>
      </div>

      {mode === "draw" ? (
        <SignaturePad value={drawing} onChange={setDrawing} />
      ) : (
        <label className="time__sign-name">
          <span>Your name</span>
          <input
            type="text"
            value={typedName}
            maxLength={120}
            autoComplete="name"
            placeholder="Type your full name"
            onChange={(e) => setTypedName(e.target.value)}
          />
        </label>
      )}

      <div className="time__sign-actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={disabled || !canSubmit}
          onClick={() =>
            void onSign(
              mode === "type"
                ? { method: "typed", typedName: typedName.trim() }
                : { method: "drawn", signatureImage: drawing! },
            )
          }
        >
          {busy ? "One moment…" : "I agree these hours are right"}
        </button>
        <button type="button" className="btn btn--ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * A strip to sign with a finger.
 *
 * POINTER EVENTS, NOT TOUCH EVENTS. One code path covers finger, stylus and mouse, which
 * is also what makes this drivable by a test — a touch-only implementation is a control
 * no deterministic gate can reach, and an untested signature pad on the one screen that
 * produces legal evidence is not a trade worth taking.
 *
 * The canvas is a FIXED pixel size rather than a scaled one. A signature captured through
 * a devicePixelRatio transform lands at a different size on every phone, and the byte
 * length is bounded by the server; a fixed 600x180 strip is small, legible and the same
 * everywhere.
 */
function SignaturePad({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const markedRef = useRef(false);

  function context(): CanvasRenderingContext2D | null {
    const ctx = canvasRef.current?.getContext("2d") ?? null;
    if (ctx) {
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#14201a";
    }
    return ctx;
  }

  function at(e: ReactPointerEvent<HTMLCanvasElement>): [number, number] {
    const rect = e.currentTarget.getBoundingClientRect();
    // The canvas is displayed at whatever width the layout gives it, so a pointer
    // coordinate has to be scaled into the fixed backing store or the ink lands somewhere
    // other than under the finger.
    return [
      ((e.clientX - rect.left) / rect.width) * e.currentTarget.width,
      ((e.clientY - rect.top) / rect.height) * e.currentTarget.height,
    ];
  }

  function start(e: ReactPointerEvent<HTMLCanvasElement>): void {
    const ctx = context();
    if (!ctx) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const [x, y] = at(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // A single tap IS a mark. Without this, a dot signature draws nothing and the button
    // stays disabled with no explanation.
    ctx.lineTo(x + 0.01, y);
    ctx.stroke();
    markedRef.current = true;
  }

  function move(e: ReactPointerEvent<HTMLCanvasElement>): void {
    if (!drawingRef.current) return;
    const ctx = context();
    if (!ctx) return;
    const [x, y] = at(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  }

  function end(): void {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (markedRef.current && canvasRef.current) {
      onChange(canvasRef.current.toDataURL("image/png"));
    }
  }

  function clear(): void {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    markedRef.current = false;
    onChange(null);
  }

  return (
    <div className="time__pad" data-state={value === null ? "empty" : "drawn"}>
      <canvas
        ref={canvasRef}
        width={600}
        height={180}
        className="time__pad-canvas"
        // Named for a screen reader, which cannot see a canvas. The typed mode beside it
        // is the accessible route; this at least says what the box is.
        role="img"
        aria-label="Signature area — draw your signature with your finger"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      />
      <div className="time__pad-footer">
        <span className="time__pad-hint">
          {value === null ? "Sign above with your finger" : "Signed"}
        </span>
        <button type="button" className="btn btn--ghost time__pad-clear" onClick={clear}>
          Clear
        </button>
      </div>
    </div>
  );
}

/** "29 Aug to 11 Sep" — the fortnight, said the way a person would say it. */
function formatPeriod(period: PayPeriod): string {
  return `${formatDateShort(period.start)} to ${formatDateShort(period.end)}`;
}

/** The person's own recent days. Nobody else's — the API returns only theirs. */
function RecentDays({ entries }: { entries: TimeEntry[] }) {
  // ef-038-2 — the same 14-day window the manual-entry form enforces on write
  // (`TimeEndpoints.MaxBackdateDays`, `ManualEntryForm`'s own `earliest` above): the read
  // side shows exactly what could still legally be entered, never history scrolling back
  // forever. Identical `new Date().toISOString().slice(0, 10)` pattern as `ManualEntryForm`
  // so both sides agree on what "14 days ago" means against the same browser clock.
  const earliest = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 14);
    return d.toISOString().slice(0, 10);
  }, []);

  // An open shift is already the subject of the big control above; repeating it here as a
  // row with no hours reads as a broken entry. A `workDate` that fails to parse at all is
  // dropped rather than shown — it can't be proven to be inside the window, and showing it
  // would read as "Invalid Date" garbage rather than a day the worker recognizes. ISO
  // `YYYY-MM-DD` strings sort lexicographically the same as chronologically, so the plain
  // `>=` below is exact — including the inclusive boundary at exactly 14 days ago.
  const days = entries.filter((e) => {
    if (e.status === "open") return false;
    if (Number.isNaN(new Date(`${e.workDate}T00:00:00`).getTime())) return false;
    return e.workDate >= earliest;
  });

  return (
    <section
      className="time__recent"
      aria-labelledby="recent-heading"
      data-state={days.length === 0 ? "empty" : "ready"}
    >
      <h2 id="recent-heading" className="time__heading time__heading--sub">
        Your recent days
      </h2>

      {days.length === 0 ? (
        <StateMessage
          tone="empty"
          title="Nothing recorded yet."
          detail="Days you clock or enter will show up here with whether your employer has approved them."
        />
      ) : (
        <ul className="time__list" aria-labelledby="recent-heading">
          {days.map((entry) => (
            <li
              key={entry.id}
              className="time__row"
              data-status={entry.status}
              // ef-024 — ground truth for a tester (ui-breadcrumbs §1): `data-entry-id`
              // is what `worker-allocation.spec.ts`'s own `recentRow` locator finds this
              // row BY, and `data-allocation` is read off `entry.allocations` rather than
              // inferred from whether a split summary happens to be rendered below.
              data-entry-id={entry.id}
              data-allocation={entry.allocations.length > 0 ? "allocated" : "unallocated"}
            >
              <span className="time__row-main">
                <span className="time__row-date">{formatDateShort(entry.workDate)}</span>
                <span className="time__row-source">
                  {/* First person here: this is the worker's own record of themselves, and
                      a person is entitled to see exactly what was stored about them —
                      which is also why they see the same coordinates their employer does,
                      rather than a vaguer version of their own position.

                      The location line REPLACES the source word for the same reason it does
                      on the farmer's queue: "Clocked · You clocked in at …" says it twice. */}
                  {entry.location === null ? (
                    entry.source === "punch" ? "Clocked" : "Entered"
                  ) : (
                    <PunchLocationLine
                      location={entry.location}
                      ended={entry.endedAt !== null}
                      voice="first"
                    />
                  )}
                </span>
                <OfflineTag entry={entry} />
              </span>

              <span className="time__row-hours">
                {entry.hours === null ? "—" : formatHours(entry.hours)}
              </span>

              <StatusChip status={entry.status} />

              {/* ef-024 — the split, read off `entry.allocations` rather than inferred,
                  same rule OfflineTag/reviewNote follow. Full width, own line, same
                  placement reasoning as `.time__row-note` below it — a list of fragments
                  squeezed into the hours column truncates on a 375px screen. READ-ONLY
                  here regardless of status: this list has never had edit controls, and
                  an approved row's split stays exactly what it was decided against.
                  Absence is deliberate — an unallocated row renders nothing extra, same
                  as the owner's own queue (`TimeApprovalQueue.tsx`), which this
                  mirrors. */}
              {entry.allocations.length > 0 && (
                <span className="time__row-allocation">
                  {entry.allocations.map((allocation, i) => (
                    <span key={allocation.jobCodeId}>
                      {i > 0 && " · "}
                      {allocation.hours.toFixed(2)} {allocation.name}
                    </span>
                  ))}
                </span>
              )}

              {/* The whole reason review_note exists. A rejection a person cannot see the
                  reason for is how a payroll dispute starts. */}
              {entry.reviewNote && (
                <p className="time__row-note">{entry.reviewNote}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const STATUS_LABEL: Record<TimeEntryStatus, string> = {
  open: "Running",
  submitted: "Waiting",
  approved: "Approved",
  rejected: "Not approved",
};

/**
 * Status is carried by TEXT, with colour as reinforcement only — the same rule the
 * dashboard's category tones follow. "Not approved" rather than "Rejected": it is the
 * same fact stated the way a person would say it to a colleague.
 */
function StatusChip({ status }: { status: TimeEntryStatus }) {
  return (
    <span className={`time__chip time__chip--${status}`}>{STATUS_LABEL[status]}</span>
  );
}

function formatHours(hours: number): string {
  // Two decimals only when the quarter-hour needs them — "8 hours" reads better than
  // "8.00 hours" on a control somebody glances at.
  const text = Number.isInteger(hours) ? String(hours) : hours.toFixed(2).replace(/0$/, "");
  return `${text} ${hours === 1 ? "hour" : "hours"}`;
}

/**
 * ef-018 — a short badge saying this day's clock times came from the phone rather than the
 * server, on the worker's own list. Kept to one word on screen — this list is scanned on
 * date/hours/status, not read line by line — but the full "how much later" fact still has
 * to reach a screen reader, which is why the accessible name carries the whole sentence
 * rather than repeating the bare visible word (ui-breadcrumbs §1: state exposed
 * semantically, not just named).
 *
 * @ui-check: same null-guard as the owner queue's `OfflineProvenance` — `receivedAt` only
 * means something alongside `recordedOffline`, and a row with neither an `endedAt` nor a
 * `startedAt` to measure against renders no tag at all rather than a delay against `null`.
 */
function OfflineTag({ entry }: { entry: TimeEntry }) {
  if (!entry.recordedOffline || entry.receivedAt === null) return null;

  const reference = entry.endedAt ?? entry.startedAt;
  if (reference === null) return null;

  return (
    <span
      className="time__offline"
      data-state="offline"
      aria-label={`Recorded offline — ${formatOfflineDelay(entry.receivedAt, reference)}`}
    >
      Offline
    </span>
  );
}

/**
 * Asks the browser where we are, and gives up cheerfully.
 *
 * NEVER REJECTS. A punch must not be blocked by GPS — a worker who cannot clock in
 * because of a location permission simply does not get paid, and that is a far worse
 * failure than a row with no coordinate. Every path here (no geolocation API at all, a
 * denied permission, no fix indoors, a slow lock) resolves to `undefined` and the punch
 * proceeds. `maximumAge` accepts a recent fix rather than forcing a fresh lock, since
 * "where this phone was a minute ago" is exactly as good an answer for a punch stamp and
 * arrives immediately.
 */
async function currentLocation(): Promise<PunchRequest | undefined> {
  if (!("geolocation" in navigator)) return undefined;

  return new Promise<PunchRequest | undefined>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
      () => resolve(undefined),
      { enableHighAccuracy: false, timeout: LOCATION_TIMEOUT_MS, maximumAge: 60_000 },
    );
  });
}
